import {
  captureException,
  capturePostHogEvent
} from 'services/posthog.service';
import {
  WEBVIEW_READY_ERROR_REASON,
  WEBVIEW_READY_STATUS,
  WEBVIEW_SCREEN
} from 'enums';
import {
  hasMacWebviewReadyHandler,
  postWebviewReadyToMac
} from 'bridge/platform-messaging.bridge';
import { getWebviewScreenForPath } from 'utils/webview-screen.util';

type EmbeddedStatusEvent =
  | 'embedded_init_start'
  | 'embedded_ready_sent'
  | 'embedded_timeout'
  | 'embedded_error';

type BasePayload = {
  screen: string;
  path?: string;
};

type ReadyPayload = BasePayload & {
  type: 'READY';
};

type TimeoutPayload = BasePayload & {
  type: 'TIMEOUT';
};

type ErrorPayload = BasePayload & {
  type: 'ERROR';
  details?: unknown;
};

type ExtraProperties = Record<string, unknown> | undefined;

// One terminal signal (ready OR error) is posted to the Mac per screen presentation.
// Tracks which screen tokens have already fired so retries/duplicate timeouts do not
// double-signal; reset on client-side navigation via resetWebviewReadyState().
const macTerminalPostedScreens = new Set<WEBVIEW_SCREEN>();

const getPathname = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.location?.pathname;
};

const postToReactNativeWebView = (message: unknown) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const target = (
      window as Window & {
        ReactNativeWebView?: { postMessage: (message: string) => void };
      }
    ).ReactNativeWebView;
    if (target && typeof target.postMessage === 'function') {
      const stringified = JSON.stringify(message);
      target.postMessage(stringified);
    }
  } catch (error) {
    captureException(error, {
      message: 'Failed to post message to ReactNativeWebView',
      extra: { message }
    });
  }
};

const hasReactNativeWebView = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const target = (
    window as Window & {
      ReactNativeWebView?: { postMessage: (message: string) => void };
    }
  ).ReactNativeWebView;
  return Boolean(target) && typeof target?.postMessage === 'function';
};

// True once a native transport that consumes the readiness handshake is attached —
// either the RN mobile bridge or the Mac webview_ready handler. Lets the caller send
// READY as soon as a shell is listening instead of waiting for RN specifically (the
// Mac has no ReactNativeWebView, so gating on RN alone would never signal the Mac).
export const isNativeReadyTransportAvailable = (): boolean =>
  hasReactNativeWebView() || hasMacWebviewReadyHandler();

// Posts a single terminal readiness signal to the Mac for the given screen, mapping the
// pathname to its short token. No-ops for screens outside the handshake or when a
// terminal signal has already been sent for that screen this presentation.
const postWebviewReadyStatusToMac = (
  pathnameScreen: string,
  status: WEBVIEW_READY_STATUS,
  reason?: WEBVIEW_READY_ERROR_REASON
) => {
  const screen = getWebviewScreenForPath(pathnameScreen);
  if (!screen || macTerminalPostedScreens.has(screen)) {
    return;
  }
  macTerminalPostedScreens.add(screen);
  postWebviewReadyToMac({ screen, status, ...(reason ? { reason } : {}) });
};

// Clears the terminal-signal guard so the next screen presentation can post again.
// Called on client-side navigation (window.fbNavigate).
export const resetWebviewReadyState = () => {
  macTerminalPostedScreens.clear();
};

const trackEmbeddedEvent = (
  event: EmbeddedStatusEvent,
  properties: BasePayload & ExtraProperties
) => {
  capturePostHogEvent(event, properties);
};

const buildBasePayload = (
  screen: string,
  extra?: ExtraProperties
): BasePayload & ExtraProperties => ({
  screen,
  path: getPathname(),
  ...extra
});

export const sendReady = (screen: string, extra?: ExtraProperties) => {
  const payload: ReadyPayload = {
    type: 'READY',
    ...buildBasePayload(screen, extra)
  };

  postToReactNativeWebView(payload);
  postWebviewReadyStatusToMac(screen, WEBVIEW_READY_STATUS.READY);
  trackEmbeddedEvent('embedded_ready_sent', payload);
};

export const sendTimeout = (screen: string, extra?: ExtraProperties) => {
  const payload: TimeoutPayload = {
    type: 'TIMEOUT',
    ...buildBasePayload(screen, extra)
  };

  postToReactNativeWebView(payload);
  postWebviewReadyStatusToMac(
    screen,
    WEBVIEW_READY_STATUS.ERROR,
    WEBVIEW_READY_ERROR_REASON.NO_TOKEN_TIMEOUT
  );
  trackEmbeddedEvent('embedded_timeout', payload);
};

export const sendError = (
  screen: string,
  details?: unknown,
  extra?: ExtraProperties
) => {
  const payload: ErrorPayload = {
    type: 'ERROR',
    details,
    ...buildBasePayload(screen, extra)
  };

  postToReactNativeWebView(payload);
  postWebviewReadyStatusToMac(screen, WEBVIEW_READY_STATUS.ERROR);
  trackEmbeddedEvent('embedded_error', payload);
};

export const trackEmbeddedInitStart = (
  screen: string,
  extra?: ExtraProperties
) => {
  const payload = buildBasePayload(screen, extra);
  trackEmbeddedEvent('embedded_init_start', payload);
};
